import { useTranslation } from '@doccareplus/i18n';

export type BookingStep = 'register' | 'details';

/**
 * Visual 2-step progress rail for the booking drawer's register-patient stepper (mirrors the staff
 * invite flow's InviteProgressStepper). Only rendered once a caller has entered the register step —
 * picking an existing patient never shows this, it's a single-view form as before.
 */
export function BookingStepIndicator({ step }: { step: BookingStep }) {
  const { t } = useTranslation('appointments');
  const steps: BookingStep[] = ['register', 'details'];
  const currentIndex = steps.indexOf(step);
  const progressLabel = t('booking.quickRegister.stepOf', { current: currentIndex + 1, total: steps.length });

  return (
    <ol className="flex gap-1.5" aria-label={progressLabel}>
      {steps.map((item, index) => (
        <li key={item} aria-current={index === currentIndex ? 'step' : undefined} className="min-w-0 flex-1 text-center">
          <div
            className={`h-1.5 rounded-full ${
              index < currentIndex
                ? 'bg-[var(--success-solid)]'
                : index === currentIndex
                  ? 'bg-[var(--primary)]'
                  : 'bg-[var(--surface-3)]'
            }`}
          />
          <div className={`mt-1.5 text-[9.5px] font-bold leading-tight ${index <= currentIndex ? 'text-[var(--text-2)]' : 'text-[var(--text-3)]'}`}>
            {t(`booking.quickRegister.stepper.${item}`)}
          </div>
        </li>
      ))}
    </ol>
  );
}
